// src/components/Navbar.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiUser, FiMenu, FiX } from "react-icons/fi";
import { handleLogout as logoutUtil } from "../utils/auth";
import { API_BASE } from "../config";
import { logError } from "../utils/logger";

export default function Navbar({ text = "#0F172A", isLoggedIn = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  
  // ตรวจสอบว่าอยู่ในหน้า Homepage หรือไม่
  const isHomePage = location.pathname === "/";

  // State สำหรับข้อมูล user จาก API
  const [userData, setUserData] = useState({
    email: "smartclick@gmail.com", // fallback
    firstName: "User", // fallback
    lastName: "",
  });

  // ดึงข้อมูล user จาก API /me เมื่อ login
  useEffect(() => {
    if (!isLoggedIn) return;

    const fetchUserData = async () => {
      try {
        const token = sessionStorage.getItem("access_token");
        if (!token) return;

        const res = await fetch(`${API_BASE}/me`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          setUserData({
            email: data?.email || "smartclick@gmail.com",
            firstName: data?.firstName || data?.first_name || "User",
            lastName: data?.lastName || data?.last_name || "",
          });
        }
      } catch (err) {
        // ถ้า error ให้ใช้ fallback values
        logError("Failed to fetch user data:", err);
      }
    };

    fetchUserData();
  }, [isLoggedIn]);

  const email = userData.email;
  const username = userData.firstName || "User";

  useEffect(() => {
    const onClickOutside = (e) => {
      if (!accountOpen) return;
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setAccountOpen(false);
      }
    };
    window.addEventListener("mousedown", onClickOutside);
    return () => window.removeEventListener("mousedown", onClickOutside);
  }, [accountOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    const onResize = () => {
      if (window.innerWidth > 768) setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => (document.body.style.overflow = prev);
    }
  }, [mobileOpen]);

  const handleLogout = () => {
    logoutUtil(navigate, () => {
      setAccountOpen(false);
      setMobileOpen(false);
    });
  };

  const handleLoginClick = () => {
    // ใช้ sessionStorage แทน localStorage เพื่อความปลอดภัย
    if (!sessionStorage.getItem("postLoginRedirect")) {
      const here =
        window.location.pathname + (window.location.search || "");
      sessionStorage.setItem("postLoginRedirect", here || "/");
    }
    setMobileOpen(false);
    navigate("/login");
  };

  const handleLicenseList = () => {
    setAccountOpen(false);
    setMobileOpen(false);
    navigate("/List");
  };

  const handleProfile = () => {
    setAccountOpen(false);
    setMobileOpen(false);
    navigate("/profile");
  };

  const handleChangePassword = () => {
    setAccountOpen(false);
    setMobileOpen(false);
    navigate("/change-password");
  };

  // Handle navigation สำหรับ anchor links
  const handleAnchorClick = (hash) => (e) => {
    e.preventDefault();
    setMobileOpen(false);
    
    if (isHomePage) {
      // ถ้าอยู่ในหน้า Homepage แล้ว ให้ scroll ไปที่ section
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      // ถ้าไม่ใช่หน้า Homepage ให้ navigate ไปหน้า Homepage พร้อม hash
      navigate(`/${hash}`);
    }
  };

  // Scroll to hash เมื่อ navigate มาที่ Homepage พร้อม hash
  useEffect(() => {
    if (isHomePage && location.hash) {
      // รอให้ component render เสร็จก่อน
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [isHomePage, location.hash]);

  const LinkAnchor = ({ href, children, onClick, style, ...rest }) => (
    <a
      href={href}
      onClick={(e) => {
        if (onClick) {
          e.preventDefault();
          onClick(e);
        }
      }}
      style={style}
      {...rest}
    >
      {children}
    </a>
  );

  const navLinkStyle = {
    color: text,
    textDecoration: "none",
    fontWeight: 600,
    fontSize: 14,
    padding: "8px 14px",
    borderRadius: 999,
    transition: "background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
  };

  const navHoverIn = (e) => {
    e.currentTarget.style.background = "rgba(37,99,235,0.09)";
    e.currentTarget.style.color = "#1D4ED8";
    e.currentTarget.style.boxShadow = "0 4px 10px rgba(148,163,184,0.5)";
  };

  const navHoverOut = (e) => {
    e.currentTarget.style.background = "transparent";
    e.currentTarget.style.color = text;
    e.currentTarget.style.boxShadow = "none";
  };

  return (
    <>
      <style>
        {`
        @keyframes accountMenuIn {
          0% {
            opacity: 0;
            transform: translateY(-6px) scale(0.97);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .account-menu-panel {
          animation: accountMenuIn 0.18s ease-out;
          transform-origin: top right;
        }

        .account-menu-item {
          transition:
            background 0.18s ease,
            transform 0.18s ease,
            box-shadow 0.18s ease;
        }

        .account-menu-item:hover {
          background: #38bdf8 !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(15,23,42,0.35);
        }

        .account-menu-item:active {
          transform: translateY(0);
          box-shadow: 0 3px 10px rgba(15,23,42,0.3);
        }
      `}
      </style>

      <div
        className="sticky-nav"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          height: 68,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 32px",
          borderBottom: "1px solid rgba(226,232,240,0.7)",
          background: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(16px)",
          boxShadow: "0 12px 30px rgba(15,23,42,0.15)",
          width: "100%",
        }}
      >
        <div className="content-wrapper" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", maxWidth: 1440, margin: "0 auto" }}>
        {/* Logo */}
        <a
          href="/"
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            e.preventDefault();
            navigate("/");
          }}
          aria-label="Go to homepage"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: "pointer",
            fontWeight: 800,
            fontSize: 20,
            color: "#1F376C",
            userSelect: "none",
            textDecoration: "none",
            textShadow: "0 2px 6px rgba(148,163,184,0.7)",
          }}
        >
          SMART AUDIT
        </a>

        {/* Desktop nav */}
        <nav
          className="desktop-nav"
          style={{ display: "flex", alignItems: "center", gap: 28 }}
        >
          <a
            href="#what-is"
            onClick={handleAnchorClick("#what-is")}
            style={navLinkStyle}
            onMouseEnter={navHoverIn}
            onMouseLeave={navHoverOut}
          >
            Products
          </a>
          <a
            href="#free-trial"
            onClick={handleAnchorClick("#free-trial")}
            style={navLinkStyle}
            onMouseEnter={navHoverIn}
            onMouseLeave={navHoverOut}
          >
            Free Trial
          </a>
          <a
            href="#footer"
            onClick={handleAnchorClick("#footer")}
            style={navLinkStyle}
            onMouseEnter={navHoverIn}
            onMouseLeave={navHoverOut}
          >
            Contact
          </a>

          {isLoggedIn ? (
            <div style={{ position: "relative" }}>
              <span
                ref={btnRef}
                onClick={() => setAccountOpen((v) => !v)}
                title="My Account"
                style={{
                  cursor: "pointer",
                  display: "inline-flex",
                  padding: 8,
                  borderRadius: 999,
                  transition: "background 0.2s ease, box-shadow 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(148,163,184,0.25)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 10px rgba(148,163,184,0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <FiUser size={22} style={{ color: text }} />
              </span>
              {accountOpen && (
                <div
                  ref={menuRef}
                  className="account-menu-panel"
                  style={{
                    position: "absolute",
                    top: 40,
                    right: 0,
                    width: 260,
                    zIndex: 1200,
                    borderRadius: 18,
                    overflow: "hidden",
                    boxShadow: "0 18px 40px rgba(15,23,42,0.45)",
                    background:
                      "linear-gradient(145deg, rgba(8,47,73,0.98) 0%, rgba(37,99,235,0.98) 55%, rgba(56,189,248,0.98) 100%)",
                    backdropFilter: "blur(16px)",
                  }}
                >
                  {/* Header: Username */}
                  <div
                    style={{
                      padding: "12px 16px 10px",
                      color: "#E0F2FE",
                      borderBottom: "1px solid rgba(148,163,184,0.35)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      {/* Avatar */}
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "999px",
                          background: "rgba(15,23,42,0.45)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 15,
                          color: "#F9FAFB",
                          boxShadow: "0 4px 10px rgba(15,23,42,0.55)",
                        }}
                      >
                        {username?.[0]?.toUpperCase?.() || "S"}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 11,
                            opacity: 0.9,
                            marginBottom: 2,
                          }}
                        >
                          Username
                        </div>
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: 15,
                            lineHeight: 1.2,
                            letterSpacing: 0.2,
                          }}
                        >
                          {username}
                        </div>
                        <div
                          style={{
                            marginTop: 1,
                            fontSize: 11.5,
                            opacity: 0.9,
                            textTransform: "lowercase",
                          }}
                        >
                          {email}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Menu items */}
                  <button
                    type="button"
                    className="account-menu-item"
                    onClick={handleProfile}
                    style={{
                      width: "100%",
                      padding: "11px 16px",
                      border: "none",
                      borderTop: "1px solid rgba(255,255,255,0.18)",
                      background: "rgba(59,130,246,0.96)",
                      color: "#F9FAFB",
                      fontWeight: 700,
                      fontSize: 13.5,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    Profile
                  </button>

                  <button
                    type="button"
                    className="account-menu-item"
                    onClick={handleLicenseList}
                    style={{
                      width: "100%",
                      padding: "11px 16px",
                      border: "none",
                      borderTop: "1px solid rgba(255,255,255,0.18)",
                      background: "rgba(59,130,246,0.96)",
                      color: "#F9FAFB",
                      fontWeight: 700,
                      fontSize: 13.5,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    License List
                  </button>

                  <button
                    type="button"
                    className="account-menu-item"
                    onClick={handleChangePassword}
                    style={{
                      width: "100%",
                      padding: "11px 16px",
                      border: "none",
                      borderTop: "1px solid rgba(255,255,255,0.18)",
                      background: "rgba(59,130,246,0.96)",
                      color: "#F9FAFB",
                      fontWeight: 700,
                      fontSize: 13.5,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    Change Password
                  </button>

                  <button
                    type="button"
                    className="account-menu-item"
                    onClick={handleLogout}
                    style={{
                      width: "100%",
                      padding: "11px 16px 12px",
                      border: "none",
                      borderTop: "1px solid rgba(255,255,255,0.18)",
                      background: "rgba(37,99,235,0.98)",
                      color: "#F9FAFB",
                      fontWeight: 800,
                      fontSize: 13.5,
                      cursor: "pointer",
                      textAlign: "left",
                      borderRadius: "0 0 18px 18px",
                    }}
                  >
                    Log Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <LinkAnchor
              href="/login"
              onClick={handleLoginClick}
              style={{
                ...navLinkStyle,
                cursor: "pointer",
                background: "#1F376C",
                color: "#fff",
                boxShadow: "0 4px 14px rgba(15,23,42,0.45)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#111827";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(15,23,42,0.6)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#1F376C";
                e.currentTarget.style.boxShadow =
                  "0 4px 14px rgba(15,23,42,0.45)";
              }}
            >
              Login
            </LinkAnchor>
          )}
        </nav>

        {/* Hamburger (mobile) */}
        <button
          className="hamburger-btn"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((o) => !o)}
          style={{
            display: "none",
            alignItems: "center",
            justifyContent: "center",
            width: 38,
            height: 38,
            borderRadius: 10,
            border: "1px solid #E5E7EB",
            background: "#fff",
            cursor: "pointer",
            boxShadow: "0 4px 14px rgba(15,23,42,0.25)",
            transition: "transform 0.22s ease, box-shadow 0.22s ease",
            transform: mobileOpen
              ? "rotate(90deg) scale(1.02)"
              : "rotate(0deg) scale(1)",
          }}
        >
          {mobileOpen ? <FiX size={20} /> : <FiMenu size={20} />}
        </button>
      </div>

      {/* Mobile overlay menu */}
      {mobileOpen && (
        <>
          <div
            onClick={() => setMobileOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.25)",
              zIndex: 999,
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: "fixed",
              top: 68,
              left: 0,
              right: 0,
              background: "#fff",
              borderBottom: "1px solid #E5E7EB",
              boxShadow: "0 8px 24px rgba(0,0,0,.12)",
              zIndex: 1001,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                padding: 16,
                gap: 8,
              }}
            >
              <a
                href="#what-is"
                onClick={handleAnchorClick("#what-is")}
                style={{
                  padding: 12,
                  fontWeight: 700,
                  color: "#0F172A",
                  textDecoration: "none",
                }}
              >
                Products
              </a>
              <a
                href="#free-trial"
                onClick={handleAnchorClick("#free-trial")}
                style={{
                  padding: 12,
                  fontWeight: 700,
                  color: "#0F172A",
                  textDecoration: "none",
                }}
              >
                Free Trial
              </a>
              <a
                href="#footer"
                onClick={handleAnchorClick("#footer")}
                style={{
                  padding: 12,
                  fontWeight: 700,
                  color: "#0F172A",
                  textDecoration: "none",
                }}
              >
                Contact
              </a>

              <div
                style={{
                  height: 1,
                  background: "#E5E7EB",
                  margin: "8px 0",
                }}
              />

              {isLoggedIn ? (
                <>
                  {/* Username block */}
                  <div
                    style={{
                      padding: "8px 12px",
                      color: "#0369A1",
                      fontWeight: 700,
                      fontSize: 14,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: "#6B7280",
                        marginBottom: 2,
                      }}
                    >
                      Username
                    </div>
                    <div>{username}</div>
                    <div
                      style={{
                        marginTop: 2,
                        fontSize: 12,
                        color: "#64748B",
                        fontWeight: 500,
                      }}
                    >
                      {email}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleProfile}
                    style={{
                      textAlign: "left",
                      padding: 12,
                      fontWeight: 700,
                      background: "transparent",
                      color: "#0F172A",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Profile
                  </button>

                  <button
                    type="button"
                    onClick={handleLicenseList}
                    style={{
                      textAlign: "left",
                      padding: 12,
                      fontWeight: 700,
                      background: "transparent",
                      color: "#0F172A",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    License List
                  </button>

                  <button
                    type="button"
                    onClick={handleChangePassword}
                    style={{
                      textAlign: "left",
                      padding: 12,
                      fontWeight: 700,
                      background: "transparent",
                      color: "#0F172A",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Change Password
                  </button>

                  <div
                    style={{
                      height: 1,
                      background: "#E5E7EB",
                      margin: "8px 0",
                    }}
                  />

                  <button
                    type="button"
                    onClick={handleLogout}
                    style={{
                      marginTop: 4,
                      padding: 12,
                      fontWeight: 800,
                      background: "#0EA5E9",
                      color: "#fff",
                      border: "1px solid #0891B2",
                      borderRadius: 8,
                      cursor: "pointer",
                    }}
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <button
                  onClick={handleLoginClick}
                  style={{
                    marginTop: 6,
                    padding: 12,
                    fontWeight: 800,
                    background: "#1F376C",
                    color: "#fff",
                    border: "1px solid #1a2f5a",
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </>
      )}
      </div>
    </>
  );
}
